/**
 * Map report trading-call rows and generator strategies into wallet TradingCall shapes.
 */
import type { TradingCall as ReportTradingCall } from '../types/report';
import type { TradingCall, PortfolioCallLayer, PlatformCallSource } from '../types/TradingCall';

export type OllamaStrategyInput = {
  symbol: string;
  strategy_type: string;
  rationale?: string;
  risk_notes?: string;
  invalidation?: string;
  target?: string;
  stop?: string;
  legs?: unknown[];
};

function neutralMarketConditions(): TradingCall['marketConditions'] {
  return {
    volatility: 'MEDIUM',
    trend: 'NEUTRAL',
    momentum: 'MIXED',
    volume: 'MEDIUM',
    sentiment: 'NEUTRAL',
  };
}

/** Report section "Active Trading Calls" → virtual portfolio (Elite Pulse layer). */
export function mapReportTradingCallToWalletCall(rc: ReportTradingCall): TradingCall {
  const sym = (rc.symbol || 'UNK').trim();
  const underlying = sym.split(/\s+/)[0].replace(/^\^/, '');
  const entry = Math.max(0.01, rc.entry ?? 1);
  const target = rc.target ?? entry * 1.08;
  const stop = rc.stop ?? entry * 0.92;
  const dirLower = (rc.direction || '').toLowerCase();
  const direction: 'BUY' | 'SELL' =
    dirLower.includes('short') || dirLower.includes('bear') || dirLower.includes('sell') ? 'SELL' : 'BUY';

  return {
    id: `ep_${rc.id}`,
    timestamp: new Date(rc.openedAt || Date.now()),
    symbol: sym,
    underlying,
    callType: 'OPTIONS',
    direction,
    strategy: {
      type: 'COMPLEX_SPREAD',
      details: {
        expiry: rc.dte,
        optionPrice: entry,
        targetUnderlyingPrice: target,
        stopUnderlyingPrice: stop,
      },
    },
    entryPrice: entry,
    targetPrice: target,
    stopLoss: stop,
    quantity: 1,
    confidence: rc.confidence === 'high' ? 85 : rc.confidence === 'medium' ? 60 : 50,
    sentiment: rc.direction || 'NEUTRAL',
    reasoning: [rc.rationale, rc.strategy].filter(Boolean).join(' — ') || sym,
    marketConditions: neutralMarketConditions(),
    status: 'ACTIVE',
    tags: ['elite_pulse'],
    notes: rc.strikes ? `Strikes: ${rc.strikes}` : undefined,
    portfolioLayer: 'elite_pulse' as PortfolioCallLayer,
  };
}

/** Ollama / generator strategy row → virtual portfolio (platform layer, Ollama source). */
export function mapOllamaStrategyToWalletCall(
  s: OllamaStrategyInput,
  index: number,
  strategyRecommendationId?: string,
): TradingCall {
  const underlying = (s.symbol || 'SPY').trim().split(/\s+/)[0].replace(/^\^/, '');
  const sym = `${underlying} ${s.strategy_type}`.trim();
  let entry = 2;
  if (Array.isArray(s.legs) && s.legs.length) {
    let sum = 0;
    for (const leg of s.legs) {
      const rec = leg as Record<string, unknown>;
      const p = Number(rec.premium ?? rec.price ?? rec.debit);
      if (Number.isFinite(p) && p > 0) sum += p;
    }
    if (sum > 0) entry = Math.max(0.25, sum);
  }
  const target = entry * 1.15;
  const stop = entry * 0.85;

  return {
    id: `gen_${index}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
    timestamp: new Date(),
    symbol: sym,
    underlying,
    callType: 'OPTIONS',
    direction: 'BUY',
    strategy: {
      type: 'COMPLEX_SPREAD',
      details: {
        optionPrice: entry,
        targetUnderlyingPrice: target,
        stopUnderlyingPrice: stop,
      },
    },
    entryPrice: entry,
    targetPrice: target,
    stopLoss: stop,
    quantity: 1,
    confidence: 55,
    sentiment: 'NEUTRAL',
    reasoning: [s.rationale, s.risk_notes].filter(Boolean).join(' — ') || s.strategy_type,
    marketConditions: neutralMarketConditions(),
    status: 'ACTIVE',
    tags: ['ollama', 'generator'],
    notes: [s.target && `Target: ${s.target}`, s.stop && `Stop: ${s.stop}`].filter(Boolean).join(' | ') || undefined,
    portfolioLayer: 'platform' as PortfolioCallLayer,
    platformSource: 'ollama' as PlatformCallSource,
    ...(strategyRecommendationId ? { strategyRecommendationId } : {}),
  };
}
